import { ApiBankDetails, ApiOrderNote, ApiWorker, ApiWorkerAccountDetails, ApiWorkerData, ApiWorkerDetails, ApiWorkerInvoice, ApiWorkerOrderFilter, ApiWorkerOrderOffer, ApiWorkerPostCode, ApiWorkerRate, ApiWorkerService } from "./interface.api";
import { BankDetails, OrderNote, Worker, WorkerAccountDetails, WorkerData, WorkerDetails, WorkerInvoice, WorkerOrderOffer, WorkerPostCode, WorkerRate, WorkerService } from "@/types";

import { OrderFilterParams } from "../order/interface";
import { mapOrderFilter } from "../order/mappers";

export const workerApiMapper = (api: ApiWorker): Worker => {
  return {
    active: api.IsActive,
    email: api.Email,
    id: `${api.ApplicationUserId}`,
    name: api.Name,
    type: api.WorkerType,
  }
};

export const workersApiMapper = (api: ApiWorker[]): Worker[] => {
  return api.map((worker) => workerApiMapper(worker));
};

export const workerAccountDetailsMapper = (api: ApiWorkerAccountDetails): WorkerAccountDetails => {
  return {
    accountPhoneNumber: api.AccountPhoneNumber,
    daysToPayInvoices: api.DaysToPayInvoices,
    email: api.AccountsEmailAddress,
    firstName: api.FirstName,
    isActive: api.IsActive,
    lastName: api.LastName,
    login: api.Name,
    sendEmailNotification: api.SendEmailNotification
  };
}

export const workerPostCodeMapper = (api: ApiWorkerPostCode): WorkerPostCode => {
  return {
    postCode: api.Postcode,
    workerPostcodeId: api.WorkerPostcodeId
  };
}

export const workerPostCodesMapper = (api: ApiWorkerPostCode[]): WorkerPostCode[] => {
  return api.map((postcode) => workerPostCodeMapper(postcode));
}

export const workerRateMapper = (api: ApiWorkerRate): WorkerRate => {
  return {
    applicationUserId: api.ApplicationUserId,
    rate: api.Rate,
    workerId: api.WorkerId,
    workerRateId: api.WorkerRateId
  };
}

export const workerRatesMapper = (api: ApiWorkerRate[]): WorkerRate[] => {
  return api.map((rate) => workerRateMapper(rate));
}

export const workerDetailsMapper = (api: ApiWorkerDetails): WorkerDetails => {
  return {
    addressId: api.AddressId,
    addressString: api.AddressString,
    area: api.Area,
    commutingDistance: api.CommutingDistance,
    commutingDistanceType: api.CommutingDistanceType,
    county: api.Country,
    doorNumber: api.DoorNumber,
    flat: api.Flat,
    fullTimeType: api.FullTimeType,
    googleCalendarId: api.GoogleCalendarId,
    houseName: api.HouseName,
    houseNumber: api.HouseNumber,
    notes: api.Notes,
    phoneNumber: api.PhoneNumber,
    postCode: api.PostCode,
    rating: api.Rating,
    road: api.Road,
    town: api.Town,
    websiteLink: api.WebsiteLink
  };
}

export const bankDetailsMapper = (api : ApiBankDetails): BankDetails => {
  return {
    bankAccountName: api.BankAccountName,
    bankAccountNumber: api.BankAccountNumber,
    bankName: api.BankName,
    bankSortCode: api.BankSortCode
  };
}

export const workerDetailsApiMapper = (api: ApiWorkerData): WorkerData => {
  return {
    accountDetails: workerAccountDetailsMapper(api.AccountDetails),
    bankDetails: bankDetailsMapper(api.BankDetails),
    postcodes: workerPostCodesMapper(api.Postcodes),
    rates: workerRatesMapper(api.Rates),
    workerDetails: workerDetailsMapper(api.WorkerDetails),
    workerId: api.WorkerId,
    workerType: api.WorkerType
  }
};

export const workerServiceApiMapper = (api: ApiWorkerService): WorkerService => {
  return {
    checked: api.Checked,
    id: api.OrderTypeId,
    name: api.Name
  };
};

export const workerServicesApiMapper = (api: ApiWorkerService[]): WorkerService[] => {
  return api.map((service) => workerServiceApiMapper(service));
};

export const mapApiOrderNotes = (notes: ApiOrderNote[]): OrderNote[] => {
  return notes.map((note) => ({
    content: note.Content,
    created: note.CreatedDateTime,
    id: `${note.NoteId}`,
    owner: note.OwnerName,
  }));
}

export const workerInvoiceApiMapper = (api: ApiWorkerInvoice): WorkerInvoice => {
  return {
    amount: api.Amount,
    approved: api.Approved,
    currency: api.Currency,
    dueDate: api.DueDate,
    fileName: api.FileName,
    filePath: api.FilePath,
    invoiceNumber: api.InvoiceNumber,
    paid: api.Paid,
    userName: api.UserName,
    workerInvoiceId: api.WorkerInvoiceId
  };
}

export const workerInvoicesApiMapper = (api: ApiWorkerInvoice[]): WorkerInvoice[] => {
  return api.map((invoice) => workerInvoiceApiMapper(invoice));
}

export const workerOrderFilterMapper = (api?: OrderFilterParams): ApiWorkerOrderFilter => {
  return {
    ...mapOrderFilter(api)
  };
}

export const workerOrderOfferApiMapper = (api: ApiWorkerOrderOffer): WorkerOrderOffer => {
  return {
    address: api.Address,
    amount: api.Amount,
    appointmentDate: api.AppointmentDate,
    id: api.Id,
    orderId: api.OrderId,
    services: api.Services,
    status: api.Status, 
    workerName: api.WorkerName
  }
}

export const workerOrderOffersApiMapper = (api: ApiWorkerOrderOffer[]): WorkerOrderOffer[] => {
  if (!api)
    return [];
  
  return api.map((offer) => workerOrderOfferApiMapper(offer));
}